





























import Vue from 'vue'
import {
  LIVE_DASH_BOARD_MAX_DATA_LENGTH,
  LIVE_DASH_BOARD_UPDATE_TIMESTAMP,
  LIVE_DASHBOARD_TYPES,
  titleByLiveDashboardTypes,
  unitByLiveDashboardTypes,
} from '@/constants/live-dashboard'
import moment from 'moment-timezone'
import { removeAbbr } from '@/utils/time'
import { CHART_DATE_FORMAT } from '@/components/patient-stats/constants'
import SeriesLine = echarts.EChartOption.SeriesLine
import XAxis = echarts.EChartOption.XAxis
import YAxis = echarts.EChartOption.YAxis
import { withSingularOrPluralLabel } from '@/utils/labels'

export default Vue.extend({
  props: {
    cardType: {
      type: String,
      required: true,
    },
    dataPoints: {
      type: Array,
      required: true,
    },
    timezone: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      titleByLiveDashboardTypes,
      unitByLiveDashboardTypes,
      colorByLiveDashboardTypes: {
        [LIVE_DASHBOARD_TYPES.OxygenSaturations]: this.$vuetify.theme.themes.light.teal,
        [LIVE_DASHBOARD_TYPES.PulseRates]: this.$vuetify.theme.themes.light.primary,
      },
      propPathByLiveDashboardTypes: {
        [LIVE_DASHBOARD_TYPES.OxygenSaturations]: ['patientWithLiveDataLatest', 'oxygenSaturation'],
        [LIVE_DASHBOARD_TYPES.PulseRates]: ['patientWithLiveDataLatest', 'pulseRate'],
      },
      chartOptions: null,
      liveDashBoardMaxDataLength: LIVE_DASH_BOARD_MAX_DATA_LENGTH,
    }
  },
  computed: {
    unitLabel() {
      return unitByLiveDashboardTypes[this.cardType]
    },
    color() {
      return this.colorByLiveDashboardTypes[this.cardType]
    },
    shouldDisplayGradientColor() {
      return this.cardType === LIVE_DASHBOARD_TYPES.OxygenSaturations
    },
    minY() {
      const values = this.dataPoints.map(dp => dp.value)
      return Math.min(...values)
    },
    maxY() {
      const values = this.dataPoints.map(dp => dp.value)
      return Math.max(...values)
    },
    seriesData() {
      return this.dataPoints.map((dp, index) => {
        return {
          value: [index, dp.value],
        }
      })
    },
    visualMap() {
      return this.shouldDisplayGradientColor
        ? [
            {
              show: false,
              type: 'continuous',
              seriesIndex: 0,
              min: 90,
              max: 95,
              inRange: {
                color: [this.$vuetify.theme.themes.light.burgundy, this.color],
              },
            },
          ]
        : null
    },
  },
  watch: {
    dataPoints(current, old) {
      this.$nextTick(() => {
        this.updateSeries()
      })
    },
  },
  mounted() {
    this.chartOptions = this.getFullOptions()
  },
  methods: {
    getFullOptions() {
      return {
        grid: [{ x: '10%', y: '24%', width: '80%', height: '70%' }],
        xAxis: {
          type: 'value',
          min: 0,
          max: this.liveDashBoardMaxDataLength - 1,
          axisLabel: { show: false },
          axisLine: { show: false },
          splitLine: { show: true },
        } as XAxis,
        yAxis: {
          type: 'value',
          min: this.minY,
          max: this.maxY,
          axisLabel: { show: false },
          axisLine: { show: false },
          splitLine: { show: false },
        } as YAxis,
        series: [
          {
            type: 'line',
            showAllSymbol: true,
            hoverAnimation: false,
            symbolSize: 12,
            lineStyle: {
              color: this.shouldDisplayGradientColor ? null : this.color,
            },
            itemStyle: {
              color: this.shouldDisplayGradientColor ? null : this.color,
            },
            emphasis: {
              symbolSize: 12,
            },
            label: {
              show: true,
              fontSize: 24,
              fontWeight: 'bold',
              color: this.shouldDisplayGradientColor ? null : this.color,
              backgroundColor: 'rgba(248,248,248, 1)',
              borderRadius: 4,
              padding: [4, 10],
            },
            data: this.seriesData as any,
          } as SeriesLine,
        ],
        tooltip: {
          formatter: params => {
            const dp = this.dataPoints[params.dataIndex]

            const date = moment(dp.timestamp).tz(this.timezone)
            const diffMSec = moment().diff(date, 'milliseconds')
            const diffSec = moment().diff(date, 'seconds')
            const overThreshold = diffMSec > LIVE_DASH_BOARD_UPDATE_TIMESTAMP

            if (overThreshold) {
              return date.format(removeAbbr(this.timezone, CHART_DATE_FORMAT.TIME_ONLY_ABBR))
            } else {
              const diffMin = Math.floor(diffSec / 60)
              const restDiffSec = diffSec % 60
              if (diffMin >= 1) {
                return withSingularOrPluralLabel(diffMin, 'minute', 'minutes') + ' ago'
              } else {
                return withSingularOrPluralLabel(restDiffSec, 'second', 'seconds') + ' ago'
              }
            }
          },
        },
        visualMap: this.visualMap,
      }
    },
    updateSeries() {
      this.chartOptions.yAxis.min = this.minY
      this.chartOptions.yAxis.max = this.maxY
      this.chartOptions.series[0].data = this.seriesData
    },
  },
})
